// FILE: src/components/modals/MyOrdersModal.jsx

import React from 'react';
import { X, Package, Clock, CheckCircle, Truck, MapPin } from 'lucide-react';
import { getOrderItemImage } from '../../utils/imageHelpers';
import orderHelpers from '../../utils/orderHelpers';

const MyOrdersModal = ({ isOpen, onClose, orders, addToCart, showToast, loadCart }) => {
  if (!isOpen) return null;

  // Debug logging to check order data structure
  console.log('📦 MyOrdersModal - Orders data:', orders);
  if (orders && orders.length > 0) {
    console.log('📦 First order address fields:', {
      deliveryAddress: orders[0].deliveryAddress,
      delivery_address: orders[0].delivery_address,
      address: orders[0].address
    });
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'Delivered':
        return 'bg-[#e8f7f3] text-[#03a685] border-[#b7e5d8]';
      case 'In Transit':
        return 'bg-[#fff8fa] text-[#ff3f6c] border-[#eaeaec]';
      case 'Processing':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Cancelled':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-[#535766] border-[#eaeaec]';
    }
  };

  const handleTrackOrder = async (order) => {
    try {
      console.log('🚚 Tracking order:', order);
      const orderIdentifier = order.order_number || order.id;
      console.log('🔑 Using order identifier:', orderIdentifier);
      
      const result = await orderHelpers.trackOrder(orderIdentifier);
      console.log('📦 Track result:', result);
      
      if (result.success && result.tracking) {
        const tracking = result.tracking;
        // Format tracking info for display
        const status = tracking.status || order.status || 'Unknown';
        const location = tracking.current_location || tracking.location || 'In transit';
        const estimatedDelivery = tracking.estimated_delivery || tracking.delivery_date || 'Not available';
        
        const trackingMessage = `Status: ${status}\nLocation: ${location}\nEstimated Delivery: ${estimatedDelivery}`;
        showToast && showToast(trackingMessage, 'success');
      } else {
        showToast && showToast(result.message || 'Failed to track order', 'error');
      }
    } catch (error) {
      console.error('❌ Track order error:', error);
      showToast && showToast('Failed to track order: ' + error.message, 'error');
    }
  };

  const handleReorder = async (order) => {
    try {
      const result = await orderHelpers.reorder(order.id);
      if (result.success) {
        // Reload cart from backend to reflect the new items
        if (loadCart) {
          await loadCart();
        }
        showToast && showToast('Items added to cart!', 'success');
        // Close modal and switch to cart
        onClose();
      } else {
        showToast && showToast(result.message || 'Failed to reorder', 'error');
      }
    } catch (error) {
      showToast && showToast('Failed to reorder', 'error');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'Delivered':
        return <CheckCircle size={16} />;
      case 'In Transit':
        return <Truck size={16} />;
      case 'Processing':
        return <Clock size={16} />;
      default:
        return <Package size={16} />;
    }
  };

  // Helper function to format address (handles both string and object)
  const formatAddress = (order) => {
    // Check if deliveryAddress is a string
    if (typeof order.deliveryAddress === 'string' && order.deliveryAddress) {
      return order.deliveryAddress;
    }
    
    // Check if delivery_address is a string
    if (typeof order.delivery_address === 'string' && order.delivery_address) {
      return order.delivery_address;
    }
    
    // Handle address object
    const addressObj = order.deliveryAddress || order.delivery_address || order.address;
    
    if (addressObj && typeof addressObj === 'object') {
      // If there's a full_address field, use it
      if (addressObj.full_address) {
        return addressObj.full_address;
      }
      
      // Otherwise, construct address from parts
      const parts = [
        addressObj.address,
        addressObj.landmark,
        addressObj.city,
        addressObj.state,
        addressObj.pincode
      ].filter(Boolean);
      
      return parts.length > 0 ? parts.join(', ') : 'Address not available';
    }
    
    return 'Address not available';
  };

  return (
    <div className="fixed inset-0 z-[300]" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div
        className="fashion-scope fixed bottom-0 left-0 right-0 bg-white rounded-t"
        style={{ maxWidth: 448, margin: '0 auto', maxHeight: '85vh', overflowY: 'auto', WebkitOverflowScrolling: 'touch', zIndex: 301 }}
      >
        {/* Same title treatment as the Shopping Bag bar; this is a bottom sheet
            rather than a page, so it keeps a close X instead of a back arrow. */}
        <div
          className="sticky top-0 bg-white flex items-center gap-3 px-6 py-3"
          style={{ borderBottom: '1px solid var(--fx-line)' }}
        >
          <span className="fx-label flex-1">My Orders</span>
          {orders.length > 0 && (
            <span style={{ fontSize: 13, color: 'var(--fx-ink-3)' }}>
              {orders.length} {orders.length === 1 ? 'order' : 'orders'}
            </span>
          )}
          <button
            onClick={onClose}
            className="p-1 -mr-1"
            aria-label="Close"
          >
            <X size={20} style={{ color: 'var(--fx-ink-2)' }} />
          </button>
        </div>

        <div className="p-6 pt-4">

        {orders.length === 0 ? (
          <div className="text-center py-12">
            <Package size={64} className="mx-auto text-gray-300 mb-4" />
            <p className="text-[#94969f] text-lg">No orders yet</p>
            <p className="text-[#94969f] text-sm mt-2">Start shopping to see your orders here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => (
              <div 
                key={order.id} 
                className="bg-white border border-[#eaeaec] rounded p-4 hover:border-[#ff3f6c] transition-all"
              >
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <p className="text-sm text-[#94969f]">Order #{order.id}</p>
                    <p className="text-xs text-[#94969f]">{order.date}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium border flex items-center gap-1 ${getStatusColor(order.status)}`}>
                    {getStatusIcon(order.status)}
                    {order.status}
                  </span>
                </div>

                <div className="space-y-3 mb-3">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex items-center gap-3 justify-between text-sm">
                      <div className="flex items-center gap-2 flex-1">
                        {getOrderItemImage(item, 'small')}
                        <div className="flex-1">
                          <span className="text-[#535766] font-medium block">
                            {item.name}({item.price ? `₹${item.price}` : 'Price not available'})  
                          </span>
                          <span className="text-xs text-[#94969f]">
                            Qty: {item.quantity}
                          </span>
                        </div>
                      </div>
                      <span className="text-[#282c3f] font-medium">
                        ₹{item.subtotal || (item.price * item.quantity).toFixed(2)}
                      </span>
                    </div>
                  ))}
                </div>

                {/* Delivery Details */}
                <div className="bg-gray-50 rounded-lg p-3 mb-3 border border-[#eaeaec]">
                  <div className="flex items-start gap-2 mb-2">
                    <MapPin size={14} className="text-[#94969f] mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs text-[#94969f] mb-1">Delivery Address</p>
                      <p className="text-sm text-[#282c3f] font-medium">
                        {formatAddress(order)}
                      </p>
                    </div>
                  </div>
                  {order.deliverySlot && (
                    <div className="text-xs text-[#535766] pl-6">
                      Time Slot: {order.deliverySlot}
                    </div>
                  )}
                </div>

                <div className="flex justify-between items-center pt-3 border-t border-[#eaeaec]">
                  <span className="text-sm font-semibold text-[#535766]">Total</span>
                  <span className="text-lg font-bold text-[#ff3f6c]">₹{order.total}</span>
                </div>

                <div className="flex gap-2 mt-3">
                  <button 
                    onClick={() => handleTrackOrder(order)}
                    className="flex-1 bg-[#ff3f6c] text-white py-2 rounded-lg text-sm font-medium hover:bg-[#e03a60] transition-all"
                  >
                    Track Order
                  </button>
                  <button 
                    onClick={() => handleReorder(order)}
                    className="flex-1 bg-gray-100 text-[#535766] py-2 rounded-lg text-sm font-medium hover:bg-gray-200 transition-all"
                  >
                    Reorder
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default MyOrdersModal;